import { useMemo, useRef, useState } from "react";
import { motion, useMotionValueEvent, useReducedMotion, useScroll, useTransform } from "motion/react";
import { geoNaturalEarth1 } from "d3-geo";
import world from "../data/worldDots.json";
import { AURORA_COUNTRIES, AURORA_REPORTED_COUNTRIES, Flag } from "./flags.jsx";

const clamp01 = (value) => Math.min(1, Math.max(0, value));

// Same projection as scripts/build-world-dots.mjs, so pins land on the dots.
const projection = geoNaturalEarth1().fitExtent([[10, 10], [world.width - 10, world.height - 10]], { type: "Sphere" });
const [DX, DY] = world.delhi;

const PINS = AURORA_COUNTRIES.map((country) => {
  const [x, y] = projection(country.lonLat);
  const distance = Math.hypot(x - DX, y - DY);
  const lift = Math.min(160, 40 + distance * 0.32);
  const cx = (DX + x) / 2;
  const cy = Math.min(DY, y) - lift;
  return { ...country, x, y, distance, arc: `M ${DX} ${DY} Q ${cx} ${cy} ${x} ${y}` };
})
  .filter((pin) => pin.code !== "in")
  .sort((a, b) => a.distance - b.distance);

const INDIA = AURORA_COUNTRIES.find((country) => country.code === "in");

function Arc({ pin, progress, range }) {
  const pathLength = useTransform(progress, (value) => clamp01((value - range[0]) / (range[1] - range[0])));
  return <motion.path className="reach__arc" d={pin.arc} style={{ pathLength }} />;
}

export function ReachMap() {
  const ref = useRef(null);
  const reduceMotion = useReducedMotion();
  const [lit, setLit] = useState(reduceMotion ? PINS.length : 0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.75", "end 0.55"] });
  const ranges = useMemo(() => PINS.map((_, index) => {
    const start = (index / PINS.length) * 0.82;
    return [start, start + 0.14];
  }), []);

  useMotionValueEvent(scrollYProgress, "change", (value) => {
    if (reduceMotion) return;
    const count = ranges.filter((range) => value >= range[1]).length;
    setLit((previous) => (previous === count ? previous : count));
  });

  const litCodes = new Set(["in", ...PINS.slice(0, lit).map((pin) => pin.code)]);
  const named = lit + 1;
  const done = lit >= PINS.length;

  return (
    <section className="reach" ref={ref} aria-labelledby="reach-title">
      <header className="reach__head">
        <h2 id="reach-title">From Delhi to 27+ countries.</h2>
        <div className="reach__count" aria-live="polite">
          <strong>{done ? `${AURORA_REPORTED_COUNTRIES}+` : named}</strong>
          <span>countries<br />joined Aurora</span>
        </div>
      </header>
      <p className="reach__lede">Aurora ran online. Every arc starts in Delhi.</p>
      <div className="reach__map">
        <svg viewBox={`0 0 ${world.width} ${world.height}`} role="img" aria-label="World map with arcs from Delhi to countries where Aurora participants joined from">
          <g className="reach__dots">
            {world.dots.map(([x, y, code], index) => (
              <circle key={index} cx={x} cy={y} r="2.1" className={code && litCodes.has(code) ? "is-lit" : undefined} />
            ))}
          </g>
          <g className="reach__arcs">
            {PINS.map((pin, index) => (
              reduceMotion
                ? <path key={pin.code} className="reach__arc" d={pin.arc} />
                : <Arc key={pin.code} pin={pin} progress={scrollYProgress} range={ranges[index]} />
            ))}
          </g>
          <circle className="reach__home" cx={DX} cy={DY} r="7" />
        </svg>
        <div className="reach__pins" aria-hidden="true">
          <span className="reach__pin is-home" style={{ left: `${(DX / world.width) * 100}%`, top: `${(DY / world.height) * 100}%` }}>
            <Flag code="in" shape="round" /><em>Delhi</em>
          </span>
          {PINS.map((pin, index) => (
            <span
              key={pin.code}
              className={`reach__pin ${index < lit ? "is-on" : ""} ${index === lit - 1 ? "is-current" : ""}`}
              style={{ left: `${(pin.x / world.width) * 100}%`, top: `${(pin.y / world.height) * 100}%` }}
            >
              <Flag code={pin.code} shape="round" /><em>{pin.name}</em>
            </span>
          ))}
        </div>
      </div>
      <ul className="reach__flags" aria-label="Countries Aurora participants joined from">
        {[INDIA, ...PINS].map((country) => (
          <li key={country.code} className={litCodes.has(country.code) ? "is-on" : undefined}>
            <Flag code={country.code} shape="rect" />
            <span>{country.name}</span>
          </li>
        ))}
        <li className={`reach__more ${done ? "is-on" : ""}`}>
          <span className="reach__moretile" aria-hidden="true">+12</span>
          <span>more countries</span>
        </li>
      </ul>
    </section>
  );
}
